import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFeatureAccess } from "../../contexts/FeatureAccessContext";
import { useStudentMe } from "../../modules/roster/hooks/useStudentMe";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import {
  PlayCircle,
  Star,
  Film,
  Volume2,
  BookOpen,
  Loader2,
  Maximize2,
  Minimize2,
} from "lucide-react";
import {
  fetchStories,
  fetchStoryVideoSignedUrl,
  LearningHttpError,
  resolveStoryStudioSelectedIndex,
  type Story,
} from "../../modules/learning/api/stories";
import { ROUTES } from "../../utils/constants";
import { handleSchoolFeatureDisabledIfPresent } from "../../utils/schoolFeatureDisabledStudent";

export const StoryStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const { refetch: refetchFeatureAccess } = useFeatureAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const sidFromUrl = searchParams.get("sid");
  const lessonFromUrl = searchParams.get("lesson");
  const gradeParam = searchParams.get("grade");
  const sublessonFromUrl = searchParams.get("sublesson")?.trim() ?? "";
  const bookIdFromUrl = searchParams.get("book_id")?.trim() ?? "";
  const segmentFromUrl = searchParams.get("segment")?.trim() ?? "";

  const { grade: rosterGrade } = useStudentMe();
  const parsedUrlGrade = useMemo(() => {
    if (gradeParam == null || gradeParam.trim() === "") return null;
    const n = parseInt(gradeParam, 10);
    if (Number.isNaN(n) || n < 1 || n > 12) return null;
    return n;
  }, [gradeParam]);

  const effectiveGrade = parsedUrlGrade ?? rosterGrade ?? 2;

  const lessonNoForApi = useMemo(() => {
    if (lessonFromUrl == null || lessonFromUrl.trim() === "") return undefined;
    const n = parseInt(lessonFromUrl, 10);
    if (Number.isNaN(n) || n < 1) return undefined;
    return n;
  }, [lessonFromUrl]);

  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoadError, setVideoLoadError] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLoading(true);
    fetchStories(effectiveGrade, {
      bookId: bookIdFromUrl || undefined,
      lessonNo: lessonNoForApi,
    })
      .then((list) => {
        if (cancelled) return;
        setStories(list);
      })
      .catch(async (e) => {
        if (cancelled) return;
        if (e instanceof LearningHttpError) {
          if (
            await handleSchoolFeatureDisabledIfPresent({
              status: e.status,
              bodyText: e.bodyText,
              navigate,
              refetchFeatureAccess,
              fallbackFeatureKey: "story_studio",
            })
          ) {
            return;
          }
          setError(e.message);
          return;
        }
        setError(e instanceof Error ? e.message : "Failed to load stories.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveGrade, bookIdFromUrl, lessonNoForApi]);

  useEffect(() => {
    if (stories.length === 0) {
      setSelectedIndex(0);
      return;
    }
    const idx = resolveStoryStudioSelectedIndex(stories, {
      sidFromUrl,
      lessonFromUrl,
      sublessonFromUrl,
      segmentFromUrl,
    });
    setSelectedIndex(Math.max(0, Math.min(idx, stories.length - 1)));
  }, [stories, sidFromUrl, lessonFromUrl, sublessonFromUrl, segmentFromUrl]);

  const selectedStory = useMemo(
    () =>
      stories.length > 0 && selectedIndex >= 0 && selectedIndex < stories.length
        ? stories[selectedIndex]
        : null,
    [stories, selectedIndex]
  );

  const handleSelectStory = (index: number) => {
    if (index < 0 || index >= stories.length) return;
    setSelectedIndex(index);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("sid", stories[index]._id);
      return p;
    });
  };

  const backToTutorUrl = useMemo(() => {
    const p = new URLSearchParams();
    if (lessonFromUrl) p.set("lesson", lessonFromUrl);
    if (sublessonFromUrl) p.set("sublesson", sublessonFromUrl);
    const q = p.toString();
    return q ? `${ROUTES.STUDENT_TAMIL_TUTOR}?${q}` : ROUTES.STUDENT_TAMIL_TUTOR;
  }, [lessonFromUrl, sublessonFromUrl]);

  // Fetch signed video URL from API and use it as video src
  useEffect(() => {
    if (!selectedStory?._id || !selectedStory?.video) {
      setVideoUrl(null);
      setVideoLoadError(null);
      setVideoLoading(false);
      return;
    }
    setVideoLoadError(null);
    setVideoLoading(true);
    let cancelled = false;
    fetchStoryVideoSignedUrl(selectedStory._id)
      .then((url) => {
        if (!cancelled) {
          setVideoUrl(url);
          setVideoLoading(false);
        }
      })
      .catch(async (e) => {
        if (cancelled) return;
        if (e instanceof LearningHttpError) {
          if (
            await handleSchoolFeatureDisabledIfPresent({
              status: e.status,
              bodyText: e.bodyText,
              navigate,
              refetchFeatureAccess,
              fallbackFeatureKey: "story_studio",
            })
          ) {
            setVideoLoading(false);
            return;
          }
          setVideoLoadError(e.message);
          setVideoLoading(false);
          return;
        }
        setVideoLoadError(e instanceof Error ? e.message : "Video failed to load.");
        setVideoLoading(false);
      });
    return () => {
      cancelled = true;
      setVideoUrl(null);
      setVideoLoading(false);
    };
  }, [selectedStory?._id]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const el =
        document.fullscreenElement ??
        (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement;
      setIsFullscreen(el === videoContainerRef.current);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
    };
  }, []);

  const requestFullscreen = (el: HTMLDivElement) => {
    const fn = el.requestFullscreen ?? (el as HTMLDivElement & { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen;
    if (fn) fn.call(el);
  };
  const exitFullscreen = () => {
    const fn =
      document.exitFullscreen ?? (document as Document & { webkitExitFullscreen?: () => void }).webkitExitFullscreen;
    if (fn) fn.call(document);
  };
  const handleFullscreenClick = () => {
    if (isFullscreen) exitFullscreen();
    else if (videoContainerRef.current) requestFullscreen(videoContainerRef.current);
  };

  const videoSrc = videoUrl ?? "";

  return (
    <Layout>
      <PageHeader
        title="Story Video Studio"
        subtitle={`Watch Grade ${effectiveGrade} Tamil story and rhyme videos. Pick one from the list.`}
      />

      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  {lessonFromUrl
                    ? "Return to the AI Tutor to continue this lesson."
                    : "Open the AI Tutor when you are ready to practice."}
                </p>
                <Button
                  size="sm"
                  className="shrink-0 bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={() => navigate(backToTutorUrl)}
                >
                  {lessonFromUrl ? "Back to AI Tutor" : "Open AI Tutor"}
                </Button>
              </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}
        

        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-slate-600 dark:text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span>Loading stories…</span>
          </div>
        ) : stories.length === 0 ? (
          <Card className="border border-slate-200 dark:border-slate-700">
            <CardContent className="py-12 text-center text-slate-600 dark:text-slate-400">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No stories available for Grade {effectiveGrade} yet.</p>
              <p className="text-sm mt-2">Stories appear here when your teacher adds them.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">
            <aside className="w-full lg:w-72 shrink-0">
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-xl border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Film className="w-5 h-5" />
                    Stories (Grade {effectiveGrade})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <nav className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                    {stories.map((story, index) => (
                      <button
                        key={story._id}
                        type="button"
                        onClick={() => handleSelectStory(index)}
                        className={`w-full text-left rounded-xl px-3 py-3 border transition flex items-center gap-3 ${
                          selectedIndex === index
                            ? "bg-white/20 border-yellow-400/60 ring-1 ring-yellow-400/40"
                            : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                        }`}
                      >
                        <div className="w-14 h-10 rounded-lg bg-black/30 flex items-center justify-center shrink-0">
                          <PlayCircle className="w-6 h-6 text-white/60" />
                        </div>
                        <span className="text-sm font-semibold text-white line-clamp-2 flex-1 min-w-0">
                          {story.title}
                        </span>
                        {selectedIndex === index && (
                          <span className="inline-flex h-2 w-2 rounded-full bg-yellow-400 shrink-0" />
                        )}
                      </button>
                    ))}
                  </nav>
                </CardContent>
              </Card>
            </aside>

            

            <main className="flex-1 min-w-0 space-y-6">
              <Card className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white shadow-2xl border-0 overflow-hidden">
                <CardContent className="p-0">
                  <div
                    ref={videoContainerRef}
                    className="relative aspect-video bg-black/40 rounded-t-2xl overflow-hidden"
                  >
                    {videoSrc && (
                      <button
                        type="button"
                        onClick={handleFullscreenClick}
                        className="absolute top-3 right-3 z-10 p-2 rounded-lg bg-black/50 hover:bg-black/70 text-white transition"
                        aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                      >
                        {isFullscreen ? (
                          <Minimize2 className="w-5 h-5" />
                        ) : (
                          <Maximize2 className="w-5 h-5" />
                        )}
                      </button>
                    )}
                    {videoSrc ? (
                      <video
                        key={selectedStory?._id}
                        className="w-full h-full object-contain"
                        controls
                        playsInline
                        src={videoSrc}
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : videoLoadError ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/90">
                        <PlayCircle className="w-16 h-16 text-white/50" />
                        <p className="text-sm">Video failed to load.</p>
                        <p className="text-xs text-white/70">{videoLoadError}</p>
                      </div>
                    ) : videoLoading ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/90">
                        <Loader2 className="w-12 h-12 animate-spin text-white/70" />
                        <p className="text-sm">Loading video…</p>
                      </div>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/90">
                        <PlayCircle className="w-16 h-16 text-white/50" />
                        <p className="text-sm">Video not available for this story.</p>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <h2 className="text-xl font-bold text-white drop-shadow-lg">
                        {selectedStory?.title}
                      </h2>
                      {selectedStory?.titleTamil && (
                        <p className="text-sm text-white/90">{selectedStory.titleTamil}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  {lessonFromUrl
                    ? "Return to the AI Tutor to continue this lesson."
                    : "Open the AI Tutor when you are ready to practice."}
                </p>
                <Button
                  size="sm"
                  className="shrink-0 bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={() => navigate(backToTutorUrl)}
                >
                  {lessonFromUrl ? "Back to AI Tutor" : "Open AI Tutor"}
                </Button>
              </div> */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-xl border-0">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Volume2 className="w-6 h-6" />
                      Practice
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-white/90">
                      After watching, discuss what you heard with the AI Tutor when you go back.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-xl border-0">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Star className="w-6 h-6" />
                      Your progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-white/90">
                      Watch Grade {effectiveGrade} videos and practice with the tutor.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </main>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StoryStudioPage;
